import type { ReactNode } from 'react';
import type { CitationView, RenderedReport } from '../../shared/analyst/api';
import { SIGNAL_MEANING, type SignalClass } from '../../shared/analyst/run';

/**
 * Testo del report con i riferimenti risolti: {{m:id}} diventa il valore del registro
 * (unità, n e intervallo), {{c:id}} una citazione cliccabile. Nessun numero è scritto
 * dal modello: tutti arrivano dal registro delle evidenze.
 */

const REF = /\{\{([mc]):([^}\s]+)\}\}/g;

interface RefTextProps {
  text: string;
  report: RenderedReport;
  onCitation: (c: CitationView) => void;
}

export function RefText({ text, report, onCitation }: RefTextProps) {
  const parts: ReactNode[] = [];
  let last = 0;
  for (const m of text.matchAll(REF)) {
    const at = m.index ?? 0;
    if (at > last) parts.push(text.slice(last, at));
    const kind = m[1];
    const id = m[2] as string;
    if (kind === 'm') {
      const e = report.evidence[id];
      parts.push(
        <span key={`${at}-m`} className="text-sky-200 font-medium bg-sky-900/30 rounded px-1" title={e ? `${id} · ${e.entry.fn}` : id}>
          {e ? e.formatted : `[${id}]`}
        </span>,
      );
    } else {
      const c = report.citations[id];
      parts.push(c ? (
        <button
          key={`${at}-c`}
          type="button"
          onClick={() => onCitation(c)}
          className="text-left text-amber-200 hover:text-amber-100 underline decoration-dotted underline-offset-2"
        >
          «{c.excerpt}» — {c.notes.kind === 'article' ? c.notes.domain ?? c.notes.source : `@${c.notes.author ?? '?'}`}
        </button>
      ) : <span key={`${at}-c`} className="text-red-300">[{id}]</span>);
    }
    last = at + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return <>{parts}</>;
}

const SIGNAL_STYLE: Record<SignalClass, string> = {
  STRONG: 'bg-emerald-700 text-emerald-50 border-emerald-500',
  EMERGING: 'bg-amber-700 text-amber-50 border-amber-500',
  NOISE: 'bg-gray-600 text-gray-100 border-gray-400',
  INSUFFICIENT: 'bg-red-800 text-red-50 border-red-500',
};

/** Classe di segnale; `withMeaning` aggiunge il significato (EMERGING = prove parziali, non crescita). */
export function SignalBadge({ signal, withMeaning = false }: { signal: SignalClass | null; withMeaning?: boolean }) {
  if (!signal) return null;
  return (
    <span title={SIGNAL_MEANING[signal]} className={`inline-block text-xs font-bold tracking-wide border rounded px-2 py-0.5 ${SIGNAL_STYLE[signal]}`}>
      {signal}{withMeaning && <span className="font-normal normal-case"> · {SIGNAL_MEANING[signal]}</span>}
    </span>
  );
}
